import React, { useState, useEffect } from 'react';
import { Button } from './Button';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenPrivacyModal: () => void;
  onOpenCookieModal: () => void;
}

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  country: string;
  preferredLanguage: string;
  inquiryType: string;
  subject: string;
  message: string;
}

interface FormErrors {
  fullName?: string;
  email?: string;
  country?: string;
  preferredLanguage?: string;
  message?: string;
}

const euCountries = [
  "Alemania", "Austria", "Bélgica", "Bulgaria", "Chipre", "Croacia", "Dinamarca",
  "Eslovaquia", "Eslovenia", "España", "Estonia", "Finlandia", "Francia",
  "Grecia", "Hungría", "Irlanda", "Italia", "Letonia", "Lituania", "Luxemburgo",
  "Malta", "Países Bajos", "Polonia", "Portugal", "República Checa", "Rumanía", "Suecia"
];

const preferredLanguages = [
  "Español", "Inglés", "Francés", "Alemán", "Italiano", "Portugués", "Otro"
];

const inquiryTypes = [
  "Consulta General", "Multa de Tráfico", "Multa de Aparcamiento", "Multa de Velocidad",
  "Alcoholemia", "Informe Pericial", "Representación Judicial", "Otro"
];


export const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose, onOpenPrivacyModal, onOpenCookieModal }) => {
  const initialFormData: FormData = {
    fullName: '',
    email: '',
    phone: '',
    country: '',
    preferredLanguage: '',
    inquiryType: '',
    subject: '',
    message: '',
  };

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormData);
      setErrors({});
      setIsSubmitting(false);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'El nombre completo es obligatorio.';
    if (!formData.email.trim()) {
      newErrors.email = 'El correo electrónico es obligatorio.';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El formato del correo electrónico no es válido.';
    }
    if (!formData.country) newErrors.country = 'El país es obligatorio.';
    if (!formData.preferredLanguage) newErrors.preferredLanguage = 'El idioma de preferencia es obligatorio.';
    if (!formData.message.trim()) newErrors.message = 'El mensaje es obligatorio.';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!validateForm()) {
      e.preventDefault();
      return;
    }
    setIsSubmitting(true);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50 transition-opacity duration-300"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-modal-title"
    >
      <div 
        className="bg-white p-6 sm:p-8 rounded-xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
          <h2 id="contact-modal-title" className="text-2xl font-bold text-gray-800">Contacta con Nosotros</h2>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600 text-3xl p-1"
            aria-label="Cerrar modal"
          >
            &times;
          </button>
        </div>

        <form 
          action="https://formsubmit.co/form-de-peritajes.eu@correo.mozmail.com" 
          method="POST"
          onSubmit={handleSubmit} 
          noValidate 
          className="space-y-6"
        >
            {/* FormSubmit hidden inputs */}
            <input type="hidden" name="_next" value="https://peritajes.eu/?form_submitted=true" />
            <input type="hidden" name="_subject" value="Nueva Consulta desde Peritajes.EU" />
            <input type="hidden" name="_captcha" value="false" />

            <div className="grid md:grid-cols-2 gap-x-6 gap-y-6">
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">Nombre Completo <span className="text-red-500">*</span></label>
                <input
                  type="text" name="fullName" id="fullName" value={formData.fullName} onChange={handleChange}
                  className={`w-full px-4 py-2.5 border ${errors.fullName ? 'border-red-400' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors`}
                  required aria-describedby={errors.fullName ? "fullName-error" : undefined}
                />
                {errors.fullName && <p id="fullName-error" className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Correo Electrónico <span className="text-red-500">*</span></label>
                <input
                  type="email" name="email" id="email" value={formData.email} onChange={handleChange}
                  className={`w-full px-4 py-2.5 border ${errors.email ? 'border-red-400' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors`}
                  required aria-describedby={errors.email ? "email-error" : undefined}
                />
                {errors.email && <p id="email-error" className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Teléfono (Opcional)</label>
              <input
                type="tel" name="phone" id="phone" value={formData.phone} onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-x-6 gap-y-6">
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">País <span className="text-red-500">*</span></label>
                <select
                  name="country" id="country" value={formData.country} onChange={handleChange}
                  className={`w-full px-4 py-2.5 border ${errors.country ? 'border-red-400' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors`}
                  required aria-describedby={errors.country ? "country-error" : undefined}
                >
                  <option value="">Selecciona un país...</option>
                  {euCountries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {errors.country && <p id="country-error" className="text-red-500 text-xs mt-1">{errors.country}</p>}
              </div>

              <div>
                <label htmlFor="preferredLanguage" className="block text-sm font-medium text-gray-700 mb-1">Idioma de Preferencia <span className="text-red-500">*</span></label>
                <select
                  name="preferredLanguage" id="preferredLanguage" value={formData.preferredLanguage} onChange={handleChange}
                  className={`w-full px-4 py-2.5 border ${errors.preferredLanguage ? 'border-red-400' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors`}
                  required aria-describedby={errors.preferredLanguage ? "preferredLanguage-error" : undefined}
                >
                  <option value="">Selecciona un idioma...</option>
                  {preferredLanguages.map(lang => <option key={lang} value={lang}>{lang}</option>)}
                </select>
                {errors.preferredLanguage && <p id="preferredLanguage-error" className="text-red-500 text-xs mt-1">{errors.preferredLanguage}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="inquiryType" className="block text-sm font-medium text-gray-700 mb-1">Tipo de Consulta (Opcional)</label>
              <select
                name="inquiryType" id="inquiryType" value={formData.inquiryType} onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors"
              >
                <option value="">Selecciona un tipo...</option>
                {inquiryTypes.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
            
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">Asunto (Opcional)</label>
              <input
                type="text" name="subject" id="subject" value={formData.subject} onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Mensaje <span className="text-red-500">*</span></label>
              <textarea
                name="message" id="message" rows={5} value={formData.message} onChange={handleChange}
                className={`w-full px-4 py-2.5 border ${errors.message ? 'border-red-400' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors`}
                required aria-describedby={errors.message ? "message-error" : undefined}
              />
              {errors.message && <p id="message-error" className="text-red-500 text-xs mt-1">{errors.message}</p>}
            </div>

            <div className="pt-2 text-xs text-gray-500">
              Al enviar este formulario, aceptas nuestra{' '}
              <button type="button" onClick={onOpenPrivacyModal} className="text-blue-600 hover:underline">
                Política de Privacidad
              </button> y nuestra <button type="button" onClick={onOpenCookieModal} className="text-blue-600 hover:underline">
                Política de Cookies
              </button>.
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300 focus:ring-gray-400">
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting} className="w-full sm:w-auto">
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Enviando...
                  </div>
                ) : 'Enviar Consulta'}
              </Button>
            </div>
          </form>
      </div>
    </div>
  );
};